import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  type Board,
  BoardCustomerVisibleBadge,
  boardListQueryOptions,
  companyBoardListQueryOptions,
} from '@/entities/board'
import { type Company, companyKeys, companyQueryOptions } from '@/entities/company'
import { useMyProjectIds } from '@/entities/employee'
import { type Project, projectListQueryOptions } from '@/entities/project'
import { EntityTagsEditor, entityTagsQueryOptions } from '@/entities/tag'
import { ArchiveCompanyDialog } from '@/features/archive-company'
import { ArchiveProjectDialog } from '@/features/archive-project'
import { ProjectMembers } from '@/features/assign-project-member'
import { type BoardOwnerRef, CreateBoardDialog } from '@/features/create-board'
import { CreateProjectDialog } from '@/features/create-project'
import { BoardMenu } from '@/features/manage-board'
import { MoveProjectDialog } from '@/features/move-project'
import { CompanyName } from '@/features/rename-company'
import { ProjectName } from '@/features/rename-project'
import {
  mergeCompanyOrder,
  SortableItem,
  useReorderBoards,
  useReorderProjects,
} from '@/features/reorder'
import { BoardCustomerVisibilityItem } from '@/features/set-customer-visibility'
import { useToggleProjectVisibility } from '@/features/toggle-visibility'
import { ApiError } from '@/shared/api'
import { cn } from '@/shared/lib'
import { rememberListFilters, useStickyListFilters } from '@/shared/model'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Checkbox } from '@/shared/ui/checkbox'
import { DepartmentBadges } from '@/shared/ui/department-badge'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'

interface CompanySearch {
  archived?: boolean
  hidden?: boolean
  mine?: boolean
  // Deep-link на конкретный проект компании (крошка «Проект» из списков задач):
  // страница проектов у компании одна, поэтому адресуемся параметром, а не маршрутом.
  project?: string
}

export const Route = createFileRoute('/companies_/$companyId')({
  component: CompanyPage,
  head: () => ({ meta: [{ title: 'Компания · STIT Task' }] }),
  // Тумблеры «показать архивные/скрытые проекты», «только мои» — в URL, как на хабе.
  validateSearch: (s: Record<string, unknown>): CompanySearch => ({
    ...(s.archived === true || s.archived === 'true' ? { archived: true } : {}),
    ...(s.hidden === true || s.hidden === 'true' ? { hidden: true } : {}),
    ...(s.mine === true || s.mine === 'true' ? { mine: true } : {}),
    ...(typeof s.project === 'string' && s.project !== '' ? { project: s.project } : {}),
  }),
})

const SKELETON_CARDS = ['s1', 's2']

// Общие сенсоры реордера: distance-порог (клик по строке-ссылке не запускает drag)
// + клавиатурный reorder за грип.
function useDnDSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
}

function ProjectsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {SKELETON_CARDS.map((s) => (
        <div key={s} className="h-28 animate-pulse rounded-card bg-bg-elevated" />
      ))}
    </div>
  )
}

// Плоская строка доски: (грип) + dept-точка + имя-ссылка на канбан + бейдж
// открытости заказчику + меню доски (видимость / смена отдела / удаление).
// Управление колонками живёт на самой доске.
function DeptBoardRow({
  board,
  editable,
  handle,
}: {
  board: Board
  editable: boolean
  handle?: ReactNode
}) {
  return (
    <div className="group flex items-center gap-2 py-1.5">
      {handle}
      <DepartmentBadges names={board.departments} variant="dot" />
      <Link
        to="/boards/$boardId"
        params={{ boardId: board.id }}
        className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary transition-colors hover:text-accent"
      >
        {board.name}
      </Link>
      {/* Открытость наружу читается из списка: заходить на доску, чтобы узнать,
          видит ли её заказчик, — слишком дорого для такого факта. */}
      {board.customerVisible && <BoardCustomerVisibleBadge className="shrink-0" />}
      {editable ? (
        // Пункт видимости приходит слотом: фича фичу не импортирует, композиция —
        // здесь, на странице.
        <BoardMenu
          board={board}
          extra={(close) => <BoardCustomerVisibilityItem board={board} onDone={close} />}
        />
      ) : (
        <Icon name="chevron-right" size={16} className="shrink-0 text-text-tertiary" />
      )}
    </div>
  )
}

// Список досок владельца с dnd-реордером за грип (только когда владелец
// редактируем и досок больше одной). Порядок — PUT .../boards/order, оптимистично.
function BoardsList({
  owner,
  boards,
  editable,
}: {
  owner: BoardOwnerRef
  boards: Board[]
  editable: boolean
}) {
  const reorder = useReorderBoards(owner)
  const sensors = useDnDSensors()
  const ids = boards.map((b) => b.id)

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const next = arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)))
    reorder.mutate(next)
  }

  if (!editable || boards.length < 2) {
    return (
      <div className="flex flex-col divide-y divide-border-subtle">
        {boards.map((b) => (
          <DeptBoardRow key={b.id} board={b} editable={editable} />
        ))}
      </div>
    )
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col divide-y divide-border-subtle">
          {boards.map((b) => (
            <SortableItem key={b.id} id={b.id}>
              {(handle) => <DeptBoardRow board={b} editable={editable} handle={handle} />}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

function ProjectCard({
  project,
  hidden,
  highlighted = false,
  handle,
}: {
  project: Project
  hidden: boolean
  // Пришли по крошке «Проект» из списка задач: карточку надо не только показать,
  // но и найти глазами — компаний с десятком проектов достаточно.
  highlighted?: boolean
  handle?: ReactNode
}) {
  const boardsQ = useQuery(boardListQueryOptions(project.id))
  const toggleVisibility = useToggleProjectVisibility(project.id)
  const [createBoard, setCreateBoard] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const editable = !project.archived

  const boards = boardsQ.data ?? []

  useEffect(() => {
    if (!highlighted) return
    cardRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  }, [highlighted])

  return (
    <Card
      ref={cardRef}
      className={cn(
        'flex flex-col gap-4',
        (project.archived || hidden) && 'opacity-60',
        highlighted && 'ring-2 ring-border-accent',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-2">
          {handle}
          <Icon name="kanban" size={18} className="shrink-0 text-text-tertiary" />
          <ProjectName project={project} editable={editable} />
          {project.archived && <span className="label shrink-0 text-text-tertiary">в архиве</span>}
          {hidden && <span className="label shrink-0 text-text-tertiary">скрыто</span>}
        </div>
        {editable && (
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setCreateBoard(true)}>
              <Icon name="plus" size={14} />
              Доска
            </Button>
            <Menu
              align="end"
              ariaLabel={`Действия с проектом «${project.name}»`}
              triggerClassName="text-text-tertiary hover:text-text-primary"
              trigger={<Icon name="more" size={16} />}
            >
              {(close) => (
                <>
                  <MenuItem
                    onSelect={() => {
                      setMoveOpen(true)
                      close()
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Icon name="building" size={14} />
                      Перенести в компанию…
                    </span>
                  </MenuItem>
                  <MenuItem
                    onSelect={() => {
                      toggleVisibility.mutate(!hidden)
                      close()
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Icon name={hidden ? 'eye' : 'eye-off'} size={14} />
                      {hidden ? 'Показать в task' : 'Скрыть в task'}
                    </span>
                  </MenuItem>
                  <MenuItem
                    className="text-accent hover:text-accent"
                    onSelect={() => {
                      setArchiveOpen(true)
                      close()
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Icon name="archive" size={14} />
                      Архивировать
                    </span>
                  </MenuItem>
                </>
              )}
            </Menu>
          </div>
        )}
      </div>

      <ProjectTags projectId={project.id} />

      <ProjectMembers projectId={project.id} editable={editable} />

      {boardsQ.isPending ? (
        <div className="h-10 animate-pulse rounded-control bg-bg-elevated" />
      ) : boardsQ.isError ? (
        <div className="flex items-center gap-3 text-sm text-text-tertiary">
          Не удалось загрузить доски
          <button
            type="button"
            onClick={() => void boardsQ.refetch()}
            className="text-text-secondary underline transition-colors hover:text-text-primary"
          >
            Повторить
          </button>
        </div>
      ) : boards.length === 0 ? (
        <p className="py-1.5 text-sm text-text-tertiary">
          {editable ? 'Досок пока нет — создайте первую.' : 'Досок нет'}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <span className="label">
            Доски{' '}
            <span data-numeric className="mono-num text-text-secondary">
              {boards.length}
            </span>
          </span>
          <BoardsList
            owner={{ kind: 'project', id: project.id }}
            boards={boards}
            editable={editable}
          />
        </div>
      )}

      {createBoard && (
        <CreateBoardDialog
          owner={{ kind: 'project', id: project.id }}
          onClose={() => setCreateBoard(false)}
        />
      )}
      {archiveOpen && (
        <ArchiveProjectDialog
          projectId={project.id}
          projectName={project.name}
          onClose={() => setArchiveOpen(false)}
        />
      )}
      {moveOpen && <MoveProjectDialog project={project} onClose={() => setMoveOpen(false)} />}
    </Card>
  )
}

// Доски самой компании — работа её уровня (договор, бюджет, онбординг клиента),
// которой не нужен проект. Отдельным блоком над проектами: это доски компании, а
// не ещё один проект, и держать их внутри проекта-пустышки было бы враньём.
// Заказчику такая доска не показывается — пометка «видно заказчику» доступна
// только доскам проектов.
function CompanyBoardsCard({ companyId, editable }: { companyId: string; editable: boolean }) {
  const boardsQ = useQuery(companyBoardListQueryOptions(companyId))
  const [createBoard, setCreateBoard] = useState(false)
  const boards = boardsQ.data ?? []

  // Пустой блок компании без досок — шум: кнопка создания живёт в шапке страницы.
  if (boardsQ.isPending || boardsQ.isError || (boards.length === 0 && !editable)) return null

  return (
    <Card className="mb-6 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="label">
          Доски компании{' '}
          {boards.length > 0 && (
            <span data-numeric className="mono-num text-text-secondary">
              {boards.length}
            </span>
          )}
        </span>
        {editable && (
          <Button size="sm" variant="secondary" onClick={() => setCreateBoard(true)}>
            <Icon name="plus" size={14} />
            Доска
          </Button>
        )}
      </div>

      {boards.length === 0 ? (
        <p className="text-sm text-text-tertiary">
          Досок компании пока нет — сюда идёт работа, которой не нужен проект.
        </p>
      ) : (
        <BoardsList
          owner={{ kind: 'company', id: companyId }}
          boards={boards}
          editable={editable}
        />
      )}

      {createBoard && (
        <CreateBoardDialog
          owner={{ kind: 'company', id: companyId }}
          onClose={() => setCreateBoard(false)}
        />
      )}
    </Card>
  )
}

// Теги компании/проекта (00-architecture § 5): читаются отдельной ручкой
// /api/tags/{kind}/{id}, не встроены в read-view. Клик по чипу уводит в поиск,
// отфильтрованный по тегу (образец — TaskTags на карточке задачи).
function CompanyTags({ companyId }: { companyId: string }) {
  const navigate = useNavigate()
  const tags = useQuery(entityTagsQueryOptions('company', companyId))
  if (tags.isPending) return <Spinner label="Загрузка тегов" />
  return (
    <EntityTagsEditor
      entityKind="company"
      entityId={companyId}
      tags={tags.data ?? []}
      onTagClick={(tag) => void navigate({ to: '/search', search: { tag } })}
    />
  )
}

function ProjectTags({ projectId }: { projectId: string }) {
  const navigate = useNavigate()
  const tags = useQuery(entityTagsQueryOptions('project', projectId))
  if (tags.isPending) return <Spinner label="Загрузка тегов" />
  return (
    <EntityTagsEditor
      entityKind="project"
      entityId={projectId}
      tags={tags.data ?? []}
      onTagClick={(tag) => void navigate({ to: '/search', search: { tag } })}
    />
  )
}

// Проекты компании с dnd-реордером за грип. Новый порядок группы отдаём наверх —
// страница видит одну компанию, а PUT /projects/order требует полный набор
// (сборка — mergeCompanyOrder по кэшу списка всех проектов). Под фильтром
// «Только мои» реордер выключен: список — подмножество проектов компании, а
// mergeCompanyOrder на неполной очереди продублировал бы чужие id.
function ProjectsList({
  projects,
  hiddenIds,
  highlightedId,
  reorderable,
  onReorder,
}: {
  projects: Project[]
  hiddenIds: Set<string>
  highlightedId: string | undefined
  reorderable: boolean
  onReorder: (ids: string[]) => void
}) {
  const sensors = useDnDSensors()
  const ids = projects.map((p) => p.id)

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    onReorder(arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-3">
          {projects.map((p) => (
            <SortableItem key={p.id} id={p.id} disabled={!reorderable || projects.length < 2}>
              {(handle) => (
                <ProjectCard
                  project={p}
                  hidden={hiddenIds.has(p.id)}
                  highlighted={p.id === highlightedId}
                  handle={handle}
                />
              )}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

// short — подпись для узкого экрана (см. тот же приём на хабе компаний).
function FilterToggle({
  label,
  short,
  checked,
  onChange,
}: {
  label: string
  short: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <Checkbox
      // Имя всегда полное (см. хаб компаний): укорачивается только видимый текст.
      aria-label={label}
      label={
        <span aria-hidden>
          <span className="sm:hidden">{short}</span>
          <span className="hidden sm:inline">{label}</span>
        </span>
      }
      labelClassName="text-sm text-text-secondary"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
  )
}

function CompanyPage() {
  const { companyId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const {
    archived: includeArchived,
    hidden: includeHidden,
    mine: onlyMine,
  } = useStickyListFilters(search)
  const qc = useQueryClient()
  // Deep-link работает через detail-ручку; переход с хаба — мгновенный
  // (initialData из list-кэша компаний).
  const companyQ = useQuery({
    ...companyQueryOptions(companyId),
    initialData: () =>
      qc.getQueryData<Company[]>(companyKeys.list(true))?.find((c) => c.id === companyId),
  })
  // ProjectView не несёт hidden — полный и видимый наборы, hidden выводим diff'ом.
  const projectsAllQ = useQuery(projectListQueryOptions(includeArchived, true))
  const projectsVisibleQ = useQuery(projectListQueryOptions(includeArchived, false))
  const myProjects = useMyProjectIds(onlyMine)
  const reorder = useReorderProjects()
  const [createProject, setCreateProject] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)

  // Тумблеры живут в URL совместно — патч поверх текущего набора, чтобы они не
  // затирали друг друга. Тот же набор уходит в память списков: хаб компаний
  // подхватит выбор, сделанный здесь.
  const applySearch = (patch: Partial<CompanySearch>) => {
    const next = {
      archived: includeArchived,
      hidden: includeHidden,
      mine: onlyMine,
      project: search.project,
      ...patch,
    }
    rememberListFilters({ archived: next.archived, hidden: next.hidden, mine: next.mine })
    void navigate({
      search: {
        ...(next.archived ? { archived: true } : {}),
        ...(next.hidden ? { hidden: true } : {}),
        ...(next.mine ? { mine: true } : {}),
        ...(next.project ? { project: next.project } : {}),
      },
    })
  }

  if (
    companyQ.isPending ||
    projectsAllQ.isPending ||
    projectsVisibleQ.isPending ||
    myProjects.isPending
  ) {
    return (
      <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
        <div className="mb-6 h-8 w-56 animate-pulse rounded-control bg-bg-elevated" />
        <ProjectsSkeleton />
      </div>
    )
  }

  if (companyQ.isError) {
    const notFound = companyQ.error instanceof ApiError && companyQ.error.status === 404
    return (
      <StatusScreen
        title={notFound ? 'Компания не найдена' : 'Не удалось загрузить компанию'}
        message={
          notFound ? 'Возможно, ссылка устарела или битая.' : 'Попробуйте обновить страницу.'
        }
      >
        {!notFound && (
          <Button variant="secondary" onClick={() => void companyQ.refetch()}>
            Повторить
          </Button>
        )}
        <Link
          to="/companies"
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К компаниям
        </Link>
      </StatusScreen>
    )
  }

  if (projectsAllQ.isError || projectsVisibleQ.isError) {
    return (
      <StatusScreen title="Не удалось загрузить проекты" message="Попробуйте обновить страницу.">
        <Button
          variant="secondary"
          onClick={() => {
            if (projectsAllQ.isError) void projectsAllQ.refetch()
            if (projectsVisibleQ.isError) void projectsVisibleQ.refetch()
          }}
        >
          Повторить
        </Button>
        <Link
          to="/companies"
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К компаниям
        </Link>
      </StatusScreen>
    )
  }

  const company = companyQ.data
  const editable = !company.archived
  const allProjects = projectsAllQ.data ?? []
  const visibleProjectIds = new Set((projectsVisibleQ.data ?? []).map((p) => p.id))
  const hiddenProjectIds = new Set(
    allProjects.filter((p) => !visibleProjectIds.has(p.id)).map((p) => p.id),
  )
  const base = includeHidden ? allProjects : allProjects.filter((p) => visibleProjectIds.has(p.id))
  const companyProjects = base.filter((p) => p.companyId === companyId)
  const projects = myProjects.ids
    ? companyProjects.filter((p) => myProjects.ids?.has(p.id))
    : companyProjects

  // Реордер внутри компании → полный плоский порядок видимых проектов всех компаний
  // из кэша списка (контракт ValidateFullReorder бэка; скрытые не переставляем).
  const onReorder = (ids: string[]) => {
    reorder.mutate(mergeCompanyOrder(projectsVisibleQ.data ?? [], companyId, ids))
  }

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <Link
        to="/companies"
        className="mb-2 inline-flex items-center gap-1 font-mono text-xs font-medium uppercase tracking-wide text-text-tertiary transition-colors hover:text-text-primary"
      >
        <Icon name="chevron-left" size={14} />
        Компании
      </Link>
      <header className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 items-center gap-2">
          <Icon name="building" size={20} className="shrink-0 text-text-tertiary" />
          <CompanyName company={company} editable={editable} />
          {company.archived && <span className="label shrink-0 text-text-tertiary">в архиве</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterToggle
            label="Только мои"
            short="Мои"
            checked={onlyMine}
            onChange={(v) => applySearch({ mine: v })}
          />
          <FilterToggle
            label="Показывать архивные"
            short="Архивные"
            checked={includeArchived}
            onChange={(v) => applySearch({ archived: v })}
          />
          <FilterToggle
            label="Показывать скрытые"
            short="Скрытые"
            checked={includeHidden}
            onChange={(v) => applySearch({ hidden: v })}
          />
          {editable && (
            <>
              <Button size="sm" onClick={() => setCreateProject(true)}>
                <Icon name="plus" size={14} />
                Проект
              </Button>
              <Menu
                align="end"
                ariaLabel={`Действия с компанией «${company.name}»`}
                triggerClassName="text-text-tertiary hover:text-text-primary"
                trigger={<Icon name="more" size={16} />}
              >
                {(close) => (
                  <MenuItem
                    className="text-accent hover:text-accent"
                    onSelect={() => {
                      setArchiveOpen(true)
                      close()
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Icon name="archive" size={14} />
                      Архивировать
                    </span>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </div>
      </header>

      <div className="mb-6">
        <CompanyTags companyId={company.id} />
      </div>

      <CompanyBoardsCard companyId={company.id} editable={editable} />

      {projects.length === 0 && onlyMine ? (
        <EmptyState
          icon={<Icon name="kanban" size={40} />}
          title="В этой компании нет ваших проектов"
          description="Фильтр показывает проекты, где вы участник. Участников назначают в карточке проекта."
          action={
            <Button variant="secondary" onClick={() => applySearch({ mine: false })}>
              Показать все проекты
            </Button>
          }
        />
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<Icon name="kanban" size={40} />}
          title={editable ? 'Проектов пока нет' : 'Проектов нет'}
          description={
            editable
              ? 'Проект — контейнер досок. Заведите его, затем добавьте доски: каждой можно назначить отдел из справочника.'
              : 'Компания в архиве — только для чтения.'
          }
          {...(editable
            ? {
                action: (
                  <Button onClick={() => setCreateProject(true)}>
                    <Icon name="plus" size={14} />
                    Проект
                  </Button>
                ),
              }
            : {})}
        />
      ) : (
        <ProjectsList
          projects={projects}
          hiddenIds={hiddenProjectIds}
          highlightedId={search.project}
          reorderable={!onlyMine}
          onReorder={onReorder}
        />
      )}

      {createProject && (
        <CreateProjectDialog
          defaultCompanyId={company.id}
          onClose={() => setCreateProject(false)}
        />
      )}
      {archiveOpen && (
        <ArchiveCompanyDialog
          companyId={company.id}
          companyName={company.name}
          onClose={() => setArchiveOpen(false)}
        />
      )}
    </div>
  )
}
